import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Chart } from 'react-google-charts';
import { useNavigate } from 'react-router-dom';
import { salesKpiApi } from '../../services/salesApi';
import type { PhaseTransitionData } from '../../services/salesApi';
import { formatCurrencyWithUnit } from '../../utils/formatNumber';
import { extractErrorMessage } from '../../utils/errorHandler';

interface TransitionCase {
  case_id: number;
  case_no: string;
  case_name: string;
  base_amount: number;
  current_amount: number;
  assigned_employee_name: string | null;
  account_name: string | null;
  transition_type: string;
}

interface PhaseTransitionSankeyProps {
  employeeId?: string;
}

const PhaseTransitionSankey: React.FC<PhaseTransitionSankeyProps> = ({ employeeId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transitionData, setTransitionData] = useState<PhaseTransitionData | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<string>('');

  // ダイアログ関連state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogCases, setDialogCases] = useState<TransitionCase[]>([]);
  const [dialogSource, setDialogSource] = useState('');
  const [dialogTarget, setDialogTarget] = useState('');

  // チャートコンテナのref
  const chartContainerRef = useRef<HTMLDivElement>(null);
  // クリックリスナー登録済みフラグ
  const clickListenerAddedRef = useRef(false);
  // 遷移データの参照（クロージャ問題回避用）
  const transitionsArrayRef = useRef<Array<{ source: string; target: string }>>([]);
  const fetchTransitionCasesRef = useRef<(source: string, target: string) => Promise<void>>();

  // 過去24週の選択肢を生成（週単位）
  const weekOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    const now = new Date();

    for (let i = 1; i <= 24; i++) {
      // i週間前の日曜日を計算
      const daysBack = i * 7;
      const targetDate = new Date(now);
      targetDate.setDate(now.getDate() - daysBack);

      // その週の日曜日（週の開始日）を取得
      const dayOfWeek = targetDate.getDay();
      const sunday = new Date(targetDate);
      sunday.setDate(targetDate.getDate() - dayOfWeek);

      // ISO週番号を計算
      const getISOWeek = (date: Date): number => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
      };

      const weekNum = getISOWeek(sunday);
      const year = sunday.getFullYear();

      // 週の日付範囲を表示用に計算
      const saturday = new Date(sunday);
      saturday.setDate(sunday.getDate() + 6);

      const formatDate = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;

      const value = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
      const label = `${year}年 第${weekNum}週 (${formatDate(sunday)}〜${formatDate(saturday)})`;
      options.push({ value, label });
    }
    return options;
  }, []);

  // デフォルトは先週
  useEffect(() => {
    if (weekOptions.length > 0 && !selectedWeek) {
      setSelectedWeek(weekOptions[0].value);
    }
  }, [weekOptions, selectedWeek]);

  // データ取得
  useEffect(() => {
    if (selectedWeek) {
      fetchTransitionData();
    }
  }, [selectedWeek, employeeId]);

  const fetchTransitionData = async () => {
    try {
      setLoading(true);
      setError('');

      // 選択された週の日曜日をbase_dateとして使用
      const response = await salesKpiApi.getPhaseTransitions({
        base_date: selectedWeek,
        employee_id: employeeId || undefined,
      });
      if (response.data.success) {
        setTransitionData(response.data.data);
      }
    } catch (err) {
      console.error('フェーズ遷移データ取得エラー:', err);
      setError(extractErrorMessage(err, 'データの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  // 遷移タイプのラベル取得
  const getTransitionTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      stayed: '停滞',
      executing: '実行中',
      progressed: '前進',
      regressed: '後退',
      won: '受注',
      lost: '失注',
      suspended: '中断',
      deleted: '削除',
    };
    return labels[type] || type;
  };

  // 遷移タイプの色を取得
  const getTransitionTypeColor = (type: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    const colors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
      stayed: 'warning',
      executing: 'success',
      progressed: 'primary',
      regressed: 'error',
      won: 'success',
      lost: 'error',
    };
    return colors[type] || 'default';
  };

  // 遷移データの配列（chartDataのインデックスとマッピング用）
  const transitionsArray = useMemo(() => {
    if (!transitionData) return [];
    return transitionData.transitions
      .filter((t) => t.transition_type !== 'stayed' && (t.transition_type as string) !== 'executing')
      .map((t) => ({ source: t.source, target: t.target }));
  }, [transitionData]);

  // refを最新値で更新（クロージャ問題回避）
  useEffect(() => {
    transitionsArrayRef.current = transitionsArray;
  }, [transitionsArray]);

  // 遷移詳細（案件一覧）を取得
  const fetchTransitionCases = useCallback(async (source: string, target: string) => {
    try {
      setDialogSource(source);
      setDialogTarget(target);
      setDialogOpen(true);
      setDialogLoading(true);

      const response = await salesKpiApi.getPhaseTransitionCases({
        base_date: selectedWeek,
        source,
        target,
        employee_id: employeeId || undefined,
      });

      if (response.data.success) {
        setDialogCases(response.data.data.cases);
      }
    } catch (err) {
      console.error('遷移詳細取得エラー:', err);
      setDialogCases([]);
    } finally {
      setDialogLoading(false);
    }
  }, [selectedWeek, employeeId]);

  // fetchTransitionCasesをrefに保存（クロージャ問題回避）
  useEffect(() => {
    fetchTransitionCasesRef.current = fetchTransitionCases;
  }, [fetchTransitionCases]);

  // チャートready時にクリックリスナーを設定（イベント委譲方式）
  const handleChartReady = useCallback(() => {
    if (!chartContainerRef.current || transitionsArray.length === 0) return;

    // イベント委譲：コンテナにクリックリスナーを設定（一度だけ）
    if (!clickListenerAddedRef.current) {
      clickListenerAddedRef.current = true;

      const container = chartContainerRef.current;
      container.addEventListener('click', (e) => {
        // クリック座標の全要素を取得（ツールチップがオーバーレイしていても下のpath要素を検出）
        const elementsAtPoint = document.elementsFromPoint(e.clientX, e.clientY);
        let targetPath: SVGPathElement | null = null;

        for (const el of elementsAtPoint) {
          if (el instanceof SVGPathElement) {
            const fillOpacity = el.getAttribute('fill-opacity');
            if (fillOpacity && parseFloat(fillOpacity) > 0) {
              targetPath = el;
              break;
            }
          }
        }

        if (!targetPath) return;

        // SVG内の全リンクパスを取得し、クリックされたパスのインデックスを特定
        const svg = container.querySelector('svg');
        if (!svg) return;

        const allPaths = svg.querySelectorAll('path');
        const linkPaths: SVGPathElement[] = [];
        allPaths.forEach((path) => {
          const opacity = path.getAttribute('fill-opacity');
          if (opacity && parseFloat(opacity) > 0) {
            linkPaths.push(path);
          }
        });

        const clickedIndex = linkPaths.indexOf(targetPath);
        if (clickedIndex === -1) return;

        const transitions = transitionsArrayRef.current;
        if (clickedIndex < transitions.length) {
          const transition = transitions[clickedIndex];
          fetchTransitionCasesRef.current?.(transition.source, transition.target);
        }
      });
    }

    // SVG内のパス要素（リンク）にカーソルスタイルを設定
    setTimeout(() => {
      const container = chartContainerRef.current;
      if (!container) return;

      const svg = container.querySelector('svg');
      if (!svg) return;

      const paths = svg.querySelectorAll('path');
      paths.forEach((path) => {
        const fillOpacity = path.getAttribute('fill-opacity');
        if (fillOpacity && parseFloat(fillOpacity) > 0) {
          path.style.cursor = 'pointer';
        }
      });
    }, 300);
  }, [transitionsArray]);

  // 案件詳細ページへ遷移
  const handleCaseClick = (caseId: number) => {
    navigate(`/sales/cases/${caseId}`);
  };

  // Google Charts用のデータ整形
  const chartData = useMemo(() => {
    if (!transitionData || !transitionData.transitions.length) {
      return null;
    }

    // Sankeyチャート用のデータ形式: [['From', 'To', Weight, Tooltip]]
    const data: Array<[string, string, number, string]> = [
      ['From', 'To', 'Weight', { role: 'tooltip', type: 'string', p: { html: true } }] as any,
    ];

    // stayed/executing遷移を除外（同じフェーズへの遷移は循環参照エラーを引き起こすため）
    transitionData.transitions
      .filter((t) => t.transition_type !== 'stayed' && (t.transition_type as string) !== 'executing')
      .forEach((t) => {
        const tooltip = `<div style="padding:10px;white-space:nowrap;"><strong>${t.source} → ${t.target}</strong><br/>件数: ${t.value}件<br/>金額: ${formatCurrencyWithUnit(t.amount)}<br/>種別: ${getTransitionTypeLabel(t.transition_type)}<br/><em style="color:#666;">クリックで案件一覧を表示</em></div>`;
        data.push([t.source, t.target, t.value, tooltip]);
      });

    return data;
  }, [transitionData]);

  // 遷移タイプ別のサマリー
  const transitionSummary = useMemo(() => {
    if (!transitionData) return null;

    const summary = {
      stayed: { count: 0, amount: 0 },
      progressed: { count: 0, amount: 0 },
      regressed: { count: 0, amount: 0 },
      won: { count: 0, amount: 0 },
      lost: { count: 0, amount: 0 },
    };

    transitionData.transitions.forEach((t) => {
      if (summary[t.transition_type as keyof typeof summary]) {
        summary[t.transition_type as keyof typeof summary].count += t.value;
        summary[t.transition_type as keyof typeof summary].amount += t.amount;
      }
    });

    return summary;
  }, [transitionData]);

  const chartOptions = {
    sankey: {
      node: {
        colors: ['#667eea', '#f093fb', '#11998e', '#4facfe', '#fa709a', '#a8edea', '#fbc2eb'],
        label: {
          fontName: 'Arial',
          fontSize: 12,
          color: '#333',
          bold: true,
        },
        nodePadding: 20,
      },
      link: {
        colorMode: 'gradient',
        colors: ['#667eea', '#f093fb', '#11998e', '#4facfe', '#fa709a'],
      },
    },
    tooltip: {
      isHtml: true,
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* 週選択 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">フェーズ遷移分析</Typography>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <Select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(e.target.value)}
            displayEmpty
          >
            {weekOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* サマリーカード */}
      {transitionSummary && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Card sx={{ flex: 1, minWidth: 120, bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
              <Typography variant="caption" color="text.secondary">前進</Typography>
              <Typography variant="h6" color="primary">{transitionSummary.progressed.count}件</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 120, bgcolor: '#fff3e0' }}>
            <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
              <Typography variant="caption" color="text.secondary">停滞</Typography>
              <Typography variant="h6" color="warning.main">{transitionSummary.stayed.count}件</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 120, bgcolor: '#ffebee' }}>
            <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
              <Typography variant="caption" color="text.secondary">後退</Typography>
              <Typography variant="h6" color="error">{transitionSummary.regressed.count}件</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 120, bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
              <Typography variant="caption" color="text.secondary">受注</Typography>
              <Typography variant="h6" color="success.main">{transitionSummary.won.count}件</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 120, bgcolor: '#fce4ec' }}>
            <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
              <Typography variant="caption" color="text.secondary">失注</Typography>
              <Typography variant="h6" sx={{ color: '#e91e63' }}>{transitionSummary.lost.count}件</Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Sankeyダイアグラム */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {chartData && chartData.length > 1 ? (
            <Box ref={chartContainerRef}>
              <Chart
                chartType="Sankey"
                width="100%"
                height="400px"
                data={chartData}
                options={chartOptions}
                chartEvents={[
                  {
                    eventName: 'ready',
                    callback: () => {
                      handleChartReady();
                    },
                  },
                ]}
              />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary">
                選択期間のフェーズ遷移データがありません
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 遷移一覧テーブル（クリック可能） */}
      {transitionData && transitionData.transitions.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              遷移一覧（クリックで案件詳細を表示）
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>遷移元</TableCell>
                    <TableCell>遷移先</TableCell>
                    <TableCell align="center">種別</TableCell>
                    <TableCell align="right">件数</TableCell>
                    <TableCell align="right">金額</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transitionData.transitions.map((t, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e3f2fd' } }}
                      onClick={() => fetchTransitionCases(t.source, t.target)}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.source}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.target}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getTransitionTypeLabel(t.transition_type)}
                          color={getTransitionTypeColor(t.transition_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{t.value}件</TableCell>
                      <TableCell align="right">{formatCurrencyWithUnit(t.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* 案件一覧ダイアログ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">
              {dialogSource} → {dialogTarget}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              該当案件: {dialogCases.length}件
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : dialogCases.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              該当する案件がありません
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>案件No</TableCell>
                    <TableCell>案件名</TableCell>
                    <TableCell>取引先</TableCell>
                    <TableCell>担当者</TableCell>
                    <TableCell align="right">基準時点金額</TableCell>
                    <TableCell align="right">現在金額</TableCell>
                    <TableCell>遷移種別</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialogCases.map((c) => (
                    <TableRow
                      key={c.case_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleCaseClick(c.case_id)}
                    >
                      <TableCell>{c.case_no}</TableCell>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.case_name}
                      </TableCell>
                      <TableCell>{c.account_name || '-'}</TableCell>
                      <TableCell>{c.assigned_employee_name || '-'}</TableCell>
                      <TableCell align="right">{formatCurrencyWithUnit(c.base_amount)}</TableCell>
                      <TableCell align="right">{formatCurrencyWithUnit(c.current_amount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getTransitionTypeLabel(c.transition_type)}
                          color={getTransitionTypeColor(c.transition_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCaseClick(c.case_id);
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhaseTransitionSankey;
